import { useMutation } from "react-query";

import axios from "axios";

import { projectServiceEndpoint } from "api/project";

import { calculateSHA256Checksum } from "../../../../../utils/checksum";

export interface IUploadProjectShapefileChunkParams {
  projectId: string;
  visibility: string;
  shapefileNodeId?: string;
  thickness: string;
  filename: string;
  chunkIndex: number;
  totalFileSize: number;
  totalChunks: number;
  uploadId?: string;
  chunk: Blob;
  isLastChunk?: boolean;
  /** Aborts the request when the user closes the modal during the upload. */
  signal?: AbortSignal;
}

export interface IUploadItemResult {
  name: string;
  outcome: "created" | "skipped" | "failed";
  reason: string;
}

export interface IUploadProjectShapefileChunkResponse {
  success: boolean;
  uploadId: string;
  chunkIndex: number;
  receivedChunks: number;
  totalChunks: number;
  fileComplete: boolean;
  processing?: boolean;
  message?: string;
  error?: string;
  // Set by the server when some of the upload did not import
  partialFailure?: boolean;
  results?: IUploadItemResult[];
}

export async function uploadProjectShapefileChunk(
  upload: IUploadProjectShapefileChunkParams
): Promise<IUploadProjectShapefileChunkResponse> {
  const formData = new FormData();
  formData.append("chunk", upload.chunk);
  formData.append("projectId", upload.projectId);
  formData.append("visibility", upload.visibility);
  formData.append("thickness", upload.thickness);
  formData.append("filename", upload.filename);
  formData.append("chunkIndex", upload.chunkIndex.toString());
  formData.append("totalFileSize", upload.totalFileSize.toString());
  formData.append("totalChunks", upload.totalChunks.toString());

  if (upload.shapefileNodeId) {
    formData.append("shapefileNodeId", upload.shapefileNodeId);
  }

  if (upload.uploadId) {
    formData.append("uploadId", upload.uploadId);
  }

  if (upload.isLastChunk !== undefined) {
    formData.append("isLastChunk", upload.isLastChunk.toString());
  }

  try {
    const checksum = await calculateSHA256Checksum(upload.chunk);
    formData.append("checksum", checksum);

    const response = await axios.post(
      `${projectServiceEndpoint}/shapefile/upload-chunk`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          "X-Checksum": checksum
        },
        signal: upload.signal
      }
    );

    return response.data;
  } catch (error) {
    if (error.message?.includes("File too large for checksum calculation")) {
      // eslint-disable-next-line no-console
      console.warn("Uploading chunk without checksum due to size:", error.message);

      const response = await axios.post(
        `${projectServiceEndpoint}/shapefile/upload-chunk`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data"
          },
          signal: upload.signal
        }
      );

      return response.data;
    }
    throw error; // Re-throw other errors
  }
}

export const useUploadProjectShapefileChunkedMutation = () => {
  return useMutation({
    mutationFn: uploadProjectShapefileChunk,
    retry: false
  });
};
